/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useState } from 'react';
import Imgix from 'react-imgix';
import { Button } from '../../UI';
import defaultImages from './defaultImages';
import useStyle from './style';

export interface ImageType {
  id: string;
  name: string;
  image: { url: string; alt: string | null };
}

const CoveEquipment: React.FC<{
  images?: ImageType[];
  staticImage?: { alt: string; url: string };
  buttonText?: string;
  mobileRows?: {
    title: string;
    text: string;
    image: { alt: string; url: string };
    group: string;
  }[];
  ctaLink?: string;
}> = ({
  images = defaultImages,
  staticImage,
  mobileRows,
  buttonText = 'Choose My Equipment',
  ctaLink = '/quiz',
}) => {
  const [groupHovered, setGroupHovered] = useState('');

  const {
    componentWrapper,
    componentContent,
    componentRows,
    componentRowWrapper,
    largeRowWrapper,
    componentRow,
    ctaStyle,
    componentImage,
    hoverImageStyle,
    staticImageStyle,
  } = useStyle();

  const getImageByName = (name: string) => {
    return (
      images?.filter(image => image.name === name)?.[0]?.image || {
        url: '',
        alt: '',
      }
    );
  };

  const equipmentRows = mobileRows || [
    {
      title: 'Hub and Alarm Panel',
      text: 'Our hub links all of your sensors to our intuitive alarm panel, keeping you connected to our three 24/7 monitoring stations, so you’ll get the help you need right away.',
      image: getImageByName('equipment-panel'),
      group: 'hub',
    },
    {
      title: 'Cove Connect App',
      text: 'Easily control your sensors, arm and disarm your system, view your camera feed, and more!',
      image: getImageByName('cove-connect-app'),
      group: 'app',
    },
    {
      title: 'Burglary and Intrusion Sensors',
      text: 'Protect your home’s entry points and common areas with our door, window, motion, and glass break sensors.',
      image: getImageByName('motion-sensor'),
      group: 'sensors',
    },
    {
      title: 'Cameras',
      text: 'Keep an eye on your home’s interior and exterior with night vision, live streaming, and 2-way communication.',
      image: getImageByName('camera'),
      group: 'cameras',
    },
    {
      title: 'Environmental Detectors',
      text: 'Safeguard your home against fire, smoke, carbon monoxide, and indoor leaks and floods.',
      image: getImageByName('smoke-detector'),
      group: 'environmental',
    },
    {
      title: 'Remotes and Panic Buttons',
      text: 'Convenient ways to instantly notify medical, fire, police services, or arm and disarm your system. ',
      image: getImageByName('medical-button'),
      group: 'remotes',
    },
  ];

  const hoverImages = [
    {
      name: 'hover-hub-new',
      style: { right: 0, bottom: 0 },
      width: 315,
      height: 229,
      group: 'hub',
    },
    {
      name: 'hover-display',
      style: { top: 57, left: 35 },
      width: 323,
      height: 189,
      group: 'hub',
    },
    {
      name: 'hover-door',
      style: { left: 181, bottom: 314 },
      width: 111,
      height: 140,
      group: 'sensors',
    },
    {
      name: 'hover-door',
      style: { left: 181, bottom: 149 },
      width: 111,
      height: 140,
      group: 'sensors',
    },
    {
      name: 'hover-motion',
      style: {
        left: 21,
        bottom: 81,
      },
      width: 128,
      height: 150,
      group: 'sensors',
    },
    {
      name: 'hover-siren',
      style: {
        right: 35,
        top: 243,
      },
      width: 150,
      height: 188,
      group: 'sensors',
    },
    {
      name: 'hover-glass-break',
      style: { right: 278, top: 38 },
      width: 158,
      height: 162,
      group: 'sensors',
    },
    {
      name: 'hover-remote',
      style: {
        right: 175,
        top: 52,
      },
      width: 95,
      height: 134,
      group: 'remotes',
    },
    {
      name: 'hover-medical',
      style: {
        left: 177,
        top: 313,
      },
      width: 119,
      height: 125,
      group: 'remotes',
    },
    {
      name: 'hover-smoke',
      style: { top: 27, right: 0 },
      width: 150,
      height: 200,
      group: 'environmental',
    },
    {
      name: 'hover-flood',
      style: {
        left: 15,
        top: 275,
      },
      width: 143,
      height: 135,
      group: 'environmental',
    },
    {
      name: 'hover-eufy-indoor',
      style: {
        left: 26,
        bottom: 282,
      },
      width: 117,
      height: 170,
      group: 'cameras',
    },
    {
      name: 'hover-outdoor',
      style: { right: 244, top: 249 },
      width: 237,
      height: 148,
      group: 'cameras',
    },
    {
      name: 'hover-doorbell-camera',
      style: { right: 213, bottom: 269 },
      width: 93,
      height: 233,
      group: 'cameras',
    },
    {
      name: 'hover-doorbell-chime',
      style: { right: 48, bottom: 267 },
      width: 126,
      height: 169,
      group: 'cameras',
    },
    {
      name: 'hover-app-new',
      style: {
        bottom: 149,
        left: 332,
      },
      width: 141,
      height: 287,
      group: 'app',
    },
    {
      name: 'hover-co',
      style: {
        bottom: 0,
        left: 130,
      },
      width: 262,
      height: 148,
      group: 'environmental',
    },
  ];

  const handleMouseOver = (group: string) => {
    setGroupHovered(group);
  };

  const handleMouseLeave = () => {
    setGroupHovered('');
  };

  return (
    <div css={componentWrapper}>
      <div css={componentContent}>
        <h2 className="text-h3 text-white">Equipment you can rely on.</h2>
        <div css={componentRows}>
          {equipmentRows.map((row, rowIndex) => {
            return (
              <div
                css={css`
                  ${componentRowWrapper}
                  ${rowIndex === 0 && largeRowWrapper}
                `}
                data-hovered={groupHovered === row.group}
                onMouseOver={() => handleMouseOver(`${row.group}`)}
                onMouseLeave={() => handleMouseLeave()}
                key={`equipment-row-${rowIndex}`}
              >
                <img
                  src={row?.image?.url || ''}
                  loading="lazy"
                  alt={row?.image?.alt || ''}
                />
                <div
                  css={componentRow}
                  data-hovered={
                    staticImage ? 'false' : groupHovered === row.group
                  }
                  onMouseOver={() => handleMouseOver(`${row.group}`)}
                  onMouseLeave={() => handleMouseLeave()}
                  key={`equipment-row-${rowIndex}`}
                >
                  <p className="text-h4">{row.title}</p>
                  <p>{row.text}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div css={ctaStyle}>
          <Button
            cypressId="cove-equipment-cta"
            href={ctaLink}
            style="outlined"
            size="large"
            color="purple"
          >
            {buttonText}
          </Button>
          <p>Takes less than 1 minute</p>
        </div>
      </div>
      <div
        css={componentImage}
        style={staticImage ? { aspectRatio: 'unset' } : {}}
      >
        {staticImage?.url ? (
          // @ts-ignore
          <Imgix
            css={staticImageStyle}
            src={staticImage?.url || ''}
            htmlAttributes={{ alt: staticImage?.alt || '', loading: 'lazy' }}
          />
        ) : (
          hoverImages.map((image, imageIndex) => {
            if (!getImageByName(image.name).url) {
              return <></>;
            }
            return (
              // @ts-ignore
              <Imgix
                key={`hover-image-${imageIndex}`}
                src={getImageByName(image.name).url || ''}
                width={image.width}
                height={image.height}
                htmlAttributes={{
                  alt: getImageByName(image.name).alt || '',
                  style: image.style,
                  onMouseEnter: () => setGroupHovered(`${image.group}`),
                  onMouseLeave: () => setGroupHovered(''),
                  'data-hovered':
                    groupHovered === image.group ? 'true' : 'false',
                  loading: 'lazy',
                }}
                css={hoverImageStyle}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

export default CoveEquipment;
